import { Component, ErrorHandler, Inject, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { NaytettavaKirjaus } from '../kirjanpito.component'

import { AlvNumeronTarkistustulos } from '@lemon/core-firebase/model/common'
import { TranslationService } from '@lemon/core-firebase/service/translation.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { AlvNumeronTarkistajaService } from 'app/_jaettu-angular/service/vat-number-check.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { BehaviorSubject, firstValueFrom } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'

interface VatLomake {
  vat: string
}

export interface KirjausMuutaVatDialogData {
  naytettavaKirjaus: NaytettavaKirjaus
  asiakas: Asiakas
}

@Component({
  templateUrl: './kirjaus.muuta-vat.dialog.html',
  styleUrls: ['./kirjaus.muuta-vat.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjausMuutaVatDialog implements OnInit {
  lomake: FieldTree<VatLomake>
  vatTunnuksenValidointitulos: BehaviorSubject<string> = new BehaviorSubject('')
  readonly vatValidointiOnnistui = signal(false)

  private uusiVat: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KirjausMuutaVatDialogData,
    private _dialogRef: MatDialogRef<KirjausMuutaVatDialog>,
    private _formValidationService: FormValidationService,
    private _alvNumeronTarkistajaService: AlvNumeronTarkistajaService,
    private _translationService: TranslationService,
    private _errorHandler: ErrorHandler,
  ) {
    const currentVat = this.data.naytettavaKirjaus.uusiVat || this.data.naytettavaKirjaus.kirjaus.vat || ''
    this.lomake = form(signal<VatLomake>({ vat: currentVat }), polku => {
      required(polku.vat)
    })

    this.uusiVat.next(currentVat)

    arvonMuutokset(this.lomake.vat).subscribe(value => {
      this.vatValidointiOnnistui.set(false)
      this.uusiVat.next(value)
    })
  }

  ngOnInit() {
    this.uusiVat.pipe(debounceTime(750), takeUntil(this._dialogRef.afterClosed())).subscribe(value => {
      const processed = this._formValidationService.processValue(value)

      if (processed) {
        if (this.uusiVat.value !== processed) {
          this.uusiVat.next(processed)
        }
        this._tarkistaAlvNumeroViesista(processed)
      } else {
        this.vatTunnuksenValidointitulos.next('')
      }
    })
  }

  async muuta() {
    if (this.lomake().invalid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    if (!this.vatValidointiOnnistui()) {
      return
    }

    const uusiVatValue = await firstValueFrom(this.uusiVat)

    if (!this.uusiVat.value || this.data.naytettavaKirjaus.kirjaus.vat === uusiVatValue) {
      this.data.naytettavaKirjaus.uusiVat = null
    } else {
      this.data.naytettavaKirjaus.uusiVat = uusiVatValue
    }
    this._dialogRef.close(true)
  }

  private async _tarkistaAlvNumeroViesista(value: string) {
    const kasitelty = this._formValidationService.processValue(value)

    this.vatValidointiOnnistui.set(false)
    this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.tarkistetaan', 'fi'))

    try {
      const maakoodi = kasitelty.substring(0, 2)
      const numero = kasitelty.substring(2)
      const vastaus = await this._alvNumeronTarkistajaService.tarkistaVatNumero(maakoodi, numero)
      if (vastaus.tulos === AlvNumeronTarkistustulos.ONNISTUI) {
        this.vatValidointiOnnistui.set(true)
        this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.oikein', 'fi', { nimi: vastaus.yrityksenNimi, osoite: vastaus.yrityksenOsoite }))
      } else if (vastaus.tulos === AlvNumeronTarkistustulos.EU_PALVELIMEN_VIRHE) {
        this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.epaonnistui-eu', 'fi'))
      } else if (vastaus.tulos === AlvNumeronTarkistustulos.MAAKOODI_VIRHEELLINEN) {
        this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.virheellinen-maakoodi', 'fi'))
      } else {
        this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.virheellinen-numero', 'fi'))
      }
    } catch (error) {
      this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.tuntematon-virhe', 'fi'))
      this._errorHandler.handleError(error)
    }
  }
}
